import React, { Suspense, lazy } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { SnackbarProvider } from "notistack";

import theme from "assets/theme";
import { ROUTES } from "constants/routes";
import Store from "./app_state/store";
import FirebaseContext from "./app_state/FirebaseContext";
import Firebase from "./app_state/firebase";

import ScrollToTop from "./FunctionalComponents/ScrollToTop";
import PublicLayout from "./routes/PublicLayout";
import Spinner from "./FunctionalComponents/Spinner";
import ViewTransitionStyles from "./ViewTransitionStyles";

// Route-level code splitting keeps the initial bundle small for mobile
// connections — the booking calendar (react-big-calendar) and Google Maps
// pull in real weight, so they're only fetched when actually visited.
const HomePage = lazy(() => import("./routes/HomePage"));
const ServiceDetailPage = lazy(() => import("./routes/ServiceDetailPage"));
const NotFoundPage = lazy(() => import("./routes/NotFoundPage"));
const CalenderHolder = lazy(() => import("./FunctionalComponents/CalenderHolder"));

// Staff side: one guarded layout (sign-in check + admin frame) around a chunk
// per screen, so none of it ships to a visitor browsing the site.
const LoginPage = lazy(() => import("./routes/LoginPage"));
const AdminLayoutPage = lazy(() => import("./routes/AdminLayoutPage"));
const AdminDashboardPage = lazy(() => import("./routes/AdminDashboardPage"));
const AdminBookingsPage = lazy(() => import("./routes/AdminBookingsPage"));
const AdminSlotsPage = lazy(() => import("./routes/AdminSlotsPage"));
const AdminTeamPage = lazy(() => import("./routes/AdminTeamPage"));

const firebase = new Firebase();

function RouteFallback() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
      <Spinner />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ViewTransitionStyles />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <BrowserRouter>
          <ScrollToTop />
          <Provider store={Store}>
            <FirebaseContext.Provider value={firebase}>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public site, inside the marketing header/footer. */}
                  <Route element={<PublicLayout />}>
                    <Route path={ROUTES.HOME} element={<HomePage />} />
                    <Route path={ROUTES.APPOINTMENTS} element={<CalenderHolder />} />
                    <Route path="/:slug" element={<ServiceDetailPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* Staff side: its own chrome, no site nav. */}
                  <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                  <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminLayoutPage />}>
                    <Route index element={<AdminDashboardPage />} />
                    <Route path={ROUTES.ADMIN_BOOKINGS} element={<AdminBookingsPage />} />
                    <Route path={ROUTES.ADMIN_SLOTS} element={<AdminSlotsPage />} />
                    <Route path={ROUTES.ADMIN_TEAM} element={<AdminTeamPage />} />
                    <Route path="*" element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
                  </Route>
                </Routes>
              </Suspense>
            </FirebaseContext.Provider>
          </Provider>
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
